/**
 * Poner el pin a mano — la salida cuando el GPS falla.
 *
 * Cierra la deuda #5: hasta hoy, sin ubicación, `AddPlace` mostraba el
 * formulario con el botón de guardar deshabilitado para siempre. Un
 * callejón sin salida documentado.
 *
 * **El pin está FIJO al centro y el mapa se mueve debajo.** Un marcador
 * arrastrable pone el dedo justo encima del punto que hay que ver.
 *
 * **No rompe integridad.** Crear un `Place` es curaduría y es libre
 * (`docs/09` §0.1, ADR-013); lo que exige visita física es la OPINIÓN, y
 * esa sigue pasando por el Visit Token con GPS real. Un pin puesto a mano
 * no publica nada.
 */

import { lazy, Suspense, useEffect, useState } from 'react';

import { Boton } from '../../shared/ui/primitives.js';
import { PMTILES_URL } from '../map/MapScreen.js';

const MapCanvas = lazy(() => import('../map/MapCanvas.js'));

/** Centro de la zona seed: Roma/Condesa/Narvarte/Del Valle (ADR-004 §C7). */
const ZONA_SEED = { lat: 19.4, lng: -99.16 };

/**
 * Cuánto se espera, tras dejar de mover, para anunciar la coordenada.
 *
 * `onCentroCambia` ya llega solo en `moveend`, así que no hay riesgo de un
 * anuncio por frame. El retardo es por OTRA cosa: con el teclado, cada
 * flecha es su propio `moveend`, y quien cruza dos cuadras pulsando ocho
 * veces se comería ocho lecturas encadenadas. 600 ms agrupa una ráfaga de
 * pulsaciones en un solo anuncio sin que se note espera al soltar.
 */
const RETARDO_ANUNCIO_MS = 600;

/**
 * La coordenada, dicha para ser ESCUCHADA.
 *
 * Dos decisiones, y las dos se notan solo con un lector de pantalla:
 *
 * **Cinco decimales (~1 m), no cuatro.** No es por precisión —el valor que
 * viaja a `onConfirmar` es el completo, sin redondear; esto es puro texto—
 * sino porque una región `aria-live` que no CAMBIA no anuncia nada. Medido
 * en Chrome: un ajuste fino de 4 m deja el texto IDÉNTICO a cuatro
 * decimales (~11 m por decimal) y el lector se queda mudo justo mientras
 * alguien afina el pin, que se oye como que el mapa no se movió. A cinco
 * decimales ese mismo ajuste sí cambia la frase.
 *
 * **"oeste" y no el signo menos.** `-99.15954` se lee "menos noventa y
 * nueve"; el menos no significa nada para quien escucha y sí suena a
 * error. El hemisferio se deduce del signo, así que sale gratis.
 */
function describirPunto(c: { lat: number; lng: number }) {
  const ns = c.lat >= 0 ? 'norte' : 'sur';
  const eo = c.lng >= 0 ? 'este' : 'oeste';
  return `Pin en ${Math.abs(c.lat).toFixed(5)} ${ns}, ${Math.abs(c.lng).toFixed(5)} ${eo}.`;
}

/**
 * Oculto a la vista, presente para el lector.
 *
 * Ni `display: none` ni `visibility: hidden`: las dos sacan el nodo del
 * árbol de accesibilidad, que es exactamente lo que aquí hace falta
 * conservar. Va oculto y no visible porque quien ve el mapa ya tiene el
 * pin — una coordenada en crudo en pantalla sería ruido para todos menos
 * para quien no puede verla.
 */
const SOLO_LECTOR = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

export interface PinPickerProps {
  /** Desde dónde empezar a mover, si se sabe algo. */
  centroInicial?: { lat: number; lng: number } | undefined;
  onConfirmar: (c: { lat: number; lng: number }) => void;
  onCancelar: () => void;
}

export function PinPicker({ centroInicial, onConfirmar, onCancelar }: PinPickerProps) {
  const inicial = centroInicial ?? ZONA_SEED;
  const [centro, setCentro] = useState(inicial);

  /*
   * Lo que oye quien no ve el mapa.
   *
   * Sin esto, un lector de pantalla anuncia "Map, region" y se acaba ahí:
   * el flujo por teclado llega hasta "puedo mover el mapa" pero nunca
   * hasta "sé dónde estoy poniendo el pin", que es el único trabajo de
   * esta pantalla. Verificado con teclado real: las flechas mueven el
   * mapa, así que el camino existía y lo que faltaba era el acuse.
   *
   * Arranca CON la posición inicial y no en blanco: quien llega por
   * teclado necesita el punto de partida: si no, la primera flecha le
   * dice a dónde llegó sin haberle dicho nunca de dónde salió. `polite`
   * espera su turno, así que no pisa la instrucción de arriba.
   */
  const [anuncio, setAnuncio] = useState(() => describirPunto(inicial));

  useEffect(() => {
    const t = setTimeout(() => setAnuncio(describirPunto(centro)), RETARDO_ANUNCIO_MS);
    return () => clearTimeout(t);
  }, [centro.lat, centro.lng]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gu-space-4)' }}>
      {/*
       * "o usa las flechas" no es un detalle de cortesía: sin esa mención,
       * el copy asume un gesto y quien navega con teclado no tiene por qué
       * adivinar que el mapa responde. El camino ya existía —MapLibre da
       * al canvas `tabindex=0` y mueve con las flechas, comprobado— pero
       * nada en la pantalla lo decía.
       */}
      <p style={{ margin: 0, color: 'var(--gu-text-secondary)' }}>
        Mueve el mapa —o usa las flechas— hasta que el pin quede sobre el
        lugar.
      </p>

      {/*
       * La región va SIEMPRE en el DOM, con el texto dentro cambiando, y
       * nunca montada/desmontada según haya anuncio. Un `aria-live` que
       * aparece ya con su contenido no se anuncia en varios lectores: la
       * región tiene que existir ANTES para que el cambio se detecte.
       *
       * `aria-atomic` para que lea la frase entera y no solo el número que
       * cambió, que fuera de contexto no significa nada.
       */}
      <p aria-live="polite" aria-atomic="true" style={SOLO_LECTOR}>
        {anuncio}
      </p>

      {/*
       * `height` en unidades absolutas y NO un `flex: 1` heredado.
       *
       * `MapCanvas` pide `height: 100%`, y un porcentaje necesita que el
       * ancestro tenga altura DEFINIDA — es el bug que colapsó el mapa a
       * 0 px en `App.tsx`. Aquí el 40vh la define, y el `minHeight` solo
       * sube el suelo en pantallas bajas sin quitarle esa definición.
       *
       * Medido en Chrome a 390x844: este div 350x338, el canvas 348x336 —
       * los 2 px del borde. Es el mapa entero, no un colapso silencioso.
       */}
      <div
        style={{
          position: 'relative',
          height: '40vh',
          minHeight: '15rem',
          borderRadius: 'var(--gu-radius-md)',
          overflow: 'hidden',
          border: '1px solid var(--gu-border-default)',
        }}
      >
        <Suspense fallback={<div style={{ width: '100%', height: '100%' }} />}>
          {/*
           * `centro={inicial}` — la constante, NO el estado `centro`. Es
           * DELIBERADO aunque parezca una incoherencia, y por eso lleva
           * comentario: es justo el tipo de cosa que alguien "corrige".
           *
           * El prop es una entrada de una sola dirección —dónde EMPEZAR— y
           * el estado es la salida —dónde quedó. Atar la entrada a la
           * salida sería realimentar el lazo: `MapCanvas` reacciona a
           * `centro` con un `setCenter`, y `setCenter` termina en `jumpTo`,
           * que emite `moveend` de forma incondicional (no comprueba si el
           * centro cambió). Sería: arrastre → moveend → setCentro → efecto
           * → setCenter → otro moveend.
           *
           * `MapCanvas` hoy corta ese lazo con una guarda por epsilon, así
           * que atarlos no colgaría la app — pero seguiría siendo un mapa
           * controlado sin ninguna razón para serlo: nadie de fuera necesita
           * moverlo, y el estado ya está aquí para el `onConfirmar`.
           */}
          <MapCanvas
            pmtilesUrl={PMTILES_URL}
            centro={inicial}
            zoomInicial={16}
            onCentroCambia={setCentro}
          />
        </Suspense>

        {/*
         * El pin. `pointerEvents: none` es lo que hace que el gesto llegue
         * al mapa que hay debajo en vez de morir aquí.
         *
         * Es un SVG y no un glifo de texto (`▾`) porque la punta tiene que
         * caer en el píxel del centro, y un carácter se posiciona por su
         * caja de línea, no por su dibujo. Medido en Chrome con Inter a
         * 2rem y `line-height: 1`: la tinta del `▾` acaba 8.6 px por encima
         * del borde inferior de su caja, así que `translate(-50%, -100%)`
         * dejaba la punta 8.6 px arriba del centro real. A zoom 16 y en esta
         * latitud eso son ~19 m: la banqueta de enfrente, que es justo lo
         * que este componente existe para no errar. Con el SVG la punta está
         * en `y=32` de un `viewBox` de 32 de alto, o sea en el borde exacto
         * de la caja, y el mismo `translate` la clava en el centro.
         */}
        <svg
          aria-hidden="true"
          width="24"
          height="32"
          viewBox="0 0 24 32"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            filter: 'drop-shadow(0 1px 3px rgba(0,0,0,.35))',
          }}
        >
          <path
            d="M12 32C12 32 22 18.5 22 11.5A10 10 0 1 0 2 11.5C2 18.5 12 32 12 32Z"
            fill="var(--gu-primary-strong)"
          />
          <circle cx="12" cy="11.5" r="3.5" fill="var(--gu-text-on-brand)" />
        </svg>
      </div>

      <Boton onClick={() => onConfirmar(centro)}>El lugar está aquí</Boton>

      {/*
       * El `Boton` secundario y no un `<button>` con estilos a mano:
       * `primitives.tsx` existe porque "cada pantalla llevaba sus propios
       * `style={{}}`, así que una corrección de espaciado había que
       * repetirla seis veces". La variante secundaria ya es exactamente
       * eso — texto en `--gu-text-link`, sin bloque de color que compita
       * con la acción primaria (`docs/06` §1.2).
       */}
      <Boton variante="secundario" onClick={onCancelar}>
        Volver a intentar con el GPS
      </Boton>
    </div>
  );
}
